import { useCallback, useMemo } from 'react'

import { type Edge as ReactFlowEdge } from '@xyflow/react'

import { buildParentMap } from './treeUtils'
import { type CustomNode } from './types'

interface UseMultiSelectProps {
  nodes: CustomNode[]
  edges: ReactFlowEdge[]
  setNodes: React.Dispatch<React.SetStateAction<CustomNode[]>>
}

const useMultiSelect = ({ nodes, edges, setNodes }: UseMultiSelectProps) => {
  const checkedNodes = useMemo(() => nodes.filter((n) => n.data.checked), [nodes])

  const onUnselectAll = useCallback(() => {
    setNodes((nds) =>
      nds.map((n) => ({
        ...n,
        data: {
          ...n.data,
          checked: false,
          multiselectDisabled: false,
        },
      }))
    )
  }, [setNodes])

  const onMultiSelectNode = useCallback(
    (nodeId: string, isChecked: boolean) => {
      const parentMap = buildParentMap(edges)

      setNodes((nds) => {
        const clickedNode = nds.find((n) => n.id === nodeId)
        const clickedNodeType = clickedNode?.data.type

        const currentlyChecked = nds.filter((n) => n.data.checked)
        const willBeCheckedAfter = isChecked
          ? [...currentlyChecked.map((n) => n.id), nodeId]
          : currentlyChecked.filter((n) => n.id !== nodeId).map((n) => n.id)

        const hasAnyChecked = willBeCheckedAfter.length > 0

        return nds.map((n) => {
          if (n.id === nodeId) {
            return {
              ...n,
              selectable: false,
              selected: false,
              data: {
                ...n.data,
                checked: isChecked,
                multiselectDisabled: false,
              },
            }
          }

          if (n.data.checked) {
            return {
              ...n,
              data: {
                ...n.data,
                multiselectDisabled: false,
              },
            }
          }

          const isSameType = n.data.type === clickedNodeType
          const shouldBeDisabled = hasAnyChecked ? !isSameType : !parentMap.has(n.id)

          return {
            ...n,
            data: {
              ...n.data,
              checked: false,
              multiselectDisabled: shouldBeDisabled,
            },
          }
        })
      })
    },
    [edges, setNodes]
  )

  return { checkedNodes, onMultiSelectNode, onUnselectAll }
}

export default useMultiSelect
